import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { registerLocaleData } from '@angular/common';
import localeEsCl from '@angular/common/locales/es-CL';

import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { defineCustomElements } from '@ionic/pwa-elements/loader';
import { addIcons } from 'ionicons';
import { mapOutline } from 'ionicons/icons';

import { AppModule } from './app/app.module';
import {
  ClaveUnicaAuthFlowService,
  rutaCallbackDesdeUrlExterna
} from './app/core/services/clave-unica-auth-flow.service';

registerLocaleData(localeEsCl, 'es-CL');

// UI web para plugins Capacitor (p. ej. Camera) al ejecutar como PWA/navegador — no aplica en binario nativo.
// Incluido en el bundle para que siga disponible offline (evitar script CDN). Ver:
// https://capacitorjs.com/docs/web/pwa-elements
defineCustomElements(window);

// Solo el menú offline muestra `map-outline`; sin registro, Ionic intenta GET /svg/map-outline.svg
// (falla sin red). El data URL queda en el bundle JS.
addIcons({ mapOutline });

// Inicializar Angular inmediatamente para renderizar contenido rápido
platformBrowserDynamic()
  .bootstrapModule(AppModule)
  .then(modRef => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    const router = modRef.injector.get(Router);
    const zone = modRef.injector.get(NgZone);
    const claveUnicaFlow = modRef.injector.get(ClaveUnicaAuthFlowService);
    void App.addListener('appUrlOpen', ({ url }) => {
      const dest = rutaCallbackDesdeUrlExterna(url);
      if (!dest) {
        return;
      }
      claveUnicaFlow.marcarReentradaRecibida();
      zone.run(() => void router.navigateByUrl(dest, { replaceUrl: true }));
    });
  })
  .catch(err => console.error('Error al inicializar Angular:', err));

async function limpiarSwYCaches(): Promise<void> {
  const regs = await navigator.serviceWorker.getRegistrations();
  await Promise.all(regs.map((r) => r.unregister()));
  if ('caches' in window) {
    const keys = await caches.keys();
    await Promise.all(keys.map((k) => caches.delete(k)));
  }
}

/**
 * PWA en navegador: Service Worker en dominio real (shell offline).
 * App Capacitor nativa: `www` empaquetado en el WebView; cámara y geolocalización por
 * @capacitor/camera y @capacitor/geolocation (no interfiere el SW). En Android el origen suele ser
 * `https://localhost` ( capacitor.config → server.androidScheme ), no debe confundirse con `ng serve`.
 * @see https://ionicframework.com/docs/native
 */
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    if (Capacitor.isNativePlatform()) {
      return;
    }

    const host = location.hostname;
    const esNavegadorLocalhost =
      host === 'localhost' ||
      host === '127.0.0.1' ||
      host === '::1';

    if (esNavegadorLocalhost) {
      void limpiarSwYCaches().catch((e) => console.warn('Limpieza SW localhost:', e));
      return;
    }

    navigator.serviceWorker.register('/sw.js')
      .then((registration) => {
        console.log('Service Worker registrado:', registration.scope);

        registration.update().catch((error) => {
          console.warn('No se pudo buscar actualización del Service Worker:', error);
        });

        setInterval(() => {
          registration.update().catch((error) => {
            console.warn('Error al revisar actualización del Service Worker:', error);
          });
        }, 60 * 1000);

        registration.addEventListener('updatefound', () => {
          const installingWorker = registration.installing;
          if (!installingWorker) return;

          installingWorker.addEventListener('statechange', () => {
            if (installingWorker.state === 'installed' && navigator.serviceWorker.controller) {
              console.log(
                'Nueva versión del Service Worker instalada; al recargar usarás la última build.'
              );
            }
          });
        });
      })
      .catch((error) => {
        console.log('Error al registrar Service Worker:', error);
      });
  });
}
